import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	Calendar,
	Clock,
	FileText,
	Loader2,
	MapPin,
} from "lucide-react";
import { useState } from "react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { getEffectiveStatus } from "@/lib/utils";

export const Route = createFileRoute("/vendor/$buyerSlug/solicitations/")({
	component: VendorSolicitationsPage,
});

function getStatusBadge(status: string) {
	switch (status) {
		case "open":
			return <Badge className="bg-green-100 text-green-700">Open</Badge>;
		case "awaiting_decision":
			return <Badge className="bg-yellow-100 text-yellow-700">Under Review</Badge>;
		case "awarded":
			return <Badge className="bg-blue-100 text-blue-700">Awarded</Badge>;
		case "cancelled":
			return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
		default:
			return <Badge variant="outline">{status}</Badge>;
	}
}

function getTypeBadge(type: string | null) {
	if (!type) return null;
	const typeLabels: Record<string, string> = {
		rfp: "RFP",
		rfq: "RFQ",
		rfi: "RFI",
		ifb: "IFB",
		itb: "ITB",
	};
	return (
		<Badge variant="outline" className="text-xs">
			{typeLabels[type] || type.toUpperCase()}
		</Badge>
	);
}

function formatDate(dateStr: string | null): string {
	if (!dateStr) return "-";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function getDaysUntilDeadline(dueDate: string | null): number | null {
	if (!dueDate) return null;
	const due = new Date(dueDate);
	const now = new Date();
	const diffTime = due.getTime() - now.getTime();
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function VendorSolicitationsPage() {
	const buyer = useBuyerContext();
	const { buyerSlug } = Route.useParams();
	const trpc = useTRPC();
	const navigate = useNavigate();
	const { user } = useAuth();

	// State for existing bid confirmation modal
	const [existingBidModal, setExistingBidModal] = useState<{
		open: boolean;
		solicitationId: string;
		solicitationTitle: string;
		bidId: string;
		bidStatus: string;
	} | null>(null);

	// Fetch public solicitations for this buyer
	const { data: solicitations, isLoading } = useQuery(
		trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyer.id,
			limit: 100,
			offset: 0,
		}),
	);

	// Fetch vendor's existing bids
	const { data: vendorBids } = useQuery({
		...trpc.vendorPortal.bids.list.queryOptions({}),
		enabled: !!user,
	});

	// Type for vendor bid
	type VendorBid = { id: string; status: string; solicitation_id: string };

	// Create a map of solicitation_id -> bid for quick lookup
	const bidsBySolicitation = new Map(
		((vendorBids ?? []) as VendorBid[]).map((bid) => [bid.solicitation_id, bid]),
	);

	// Calculate stats
	const stats = {
		total: solicitations?.length ?? 0,
		open: solicitations?.filter((s) => s.status === "open").length ?? 0,
		underReview: solicitations?.filter((s) => s.status === "awaiting_decision").length ?? 0,
		awarded: solicitations?.filter((s) => s.status === "awarded").length ?? 0,
	};

	// Handle bid button click - check for existing bid
	const handleBidClick = (solicitationId: string, solicitationTitle: string) => {
		const existingBid = bidsBySolicitation.get(solicitationId);
		if (existingBid) {
			setExistingBidModal({
				open: true,
				solicitationId,
				solicitationTitle,
				bidId: existingBid.id,
				bidStatus: existingBid.status,
			});
		} else {
			// No existing bid, navigate to new bid page
			navigate({
				to: "/vendor/$buyerSlug/bids/new/$solicitationId",
				params: { buyerSlug, solicitationId },
			});
		}
	};

	// Handle primary action from modal
	const handleExistingBidPrimary = () => {
		if (!existingBidModal) return;
		const { bidId, bidStatus } = existingBidModal;
		setExistingBidModal(null);
		if (bidStatus === "draft") {
			navigate({
				to: "/vendor/$buyerSlug/bids/$bidId/edit",
				params: { buyerSlug, bidId },
			});
		} else {
			navigate({
				to: "/vendor/$buyerSlug/bids/$bidId",
				params: { buyerSlug, bidId },
			});
		}
	};

	// Handle new bid from modal
	const handleNewBid = () => {
		if (!existingBidModal) return;
		const { solicitationId } = existingBidModal;
		setExistingBidModal(null);
		navigate({
			to: "/vendor/$buyerSlug/bids/new/$solicitationId",
			params: { buyerSlug, solicitationId },
		});
	};

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Solicitations</h1>
					<p className="text-muted-foreground">
						Browse all open solicitations from {buyer.name}
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">Total</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.total}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-green-500" />
								<span className="text-sm text-muted-foreground">Open for Bids</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.open}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-yellow-500" />
								<span className="text-sm text-muted-foreground">Under Review</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.underReview}</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-blue-500" />
								<span className="text-sm text-muted-foreground">Awarded</span>
							</div>
							<div className="text-2xl font-bold mt-1">{stats.awarded}</div>
						</CardContent>
					</Card>
				</div>

				{/* Solicitations List */}
				<Card>
					<CardHeader>
						<CardTitle>Available Solicitations</CardTitle>
						<CardDescription>
							{(solicitations ?? []).length} solicitation
							{(solicitations ?? []).length !== 1 ? "s" : ""} found
						</CardDescription>
					</CardHeader>
					<CardContent>
						{(solicitations ?? []).length === 0 ? (
							<div className="text-center py-12 text-muted-foreground">
								<FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
								<p>No solicitations available at this time</p>
							</div>
						) : (
							<div className="space-y-4">
								{(solicitations ?? []).map((sol) => {
									const effectiveStatus = getEffectiveStatus(sol.status, sol.publish_date, sol.due_date);
									const daysLeft = getDaysUntilDeadline(sol.due_date);
									const isUrgent = daysLeft !== null && daysLeft > 0 && daysLeft <= 7 && effectiveStatus === "open";
									const isPastDue = daysLeft !== null && daysLeft < 0;

									return (
										<div
											key={sol.id}
											className="border rounded-lg p-4 hover:bg-muted/30 transition-colors"
										>
											<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
												<div className="flex-1 space-y-2">
													<div className="flex items-start gap-2 flex-wrap">
														<h3 className="font-semibold text-lg">
															{sol.title}
														</h3>
														{getStatusBadge(effectiveStatus)}
														{getTypeBadge(sol.type)}
													</div>

													{sol.description && (
														<p className="text-sm text-muted-foreground line-clamp-2">
															{sol.description}
														</p>
													)}

													<div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
														{sol.due_date && (
															<div className="flex items-center gap-1">
																<Calendar className="h-4 w-4" />
																<span>Due: {formatDate(sol.due_date)}</span>
																{isUrgent && (
																	<Badge variant="destructive" className="ml-1 text-xs">
																		{daysLeft}d left
																	</Badge>
																)}
																{isPastDue && effectiveStatus === "open" && (
																	<Badge variant="outline" className="ml-1 text-xs text-muted-foreground">
																		Closed
																	</Badge>
																)}
															</div>
														)}
														{sol.address && (
															<div className="flex items-center gap-1">
																<MapPin className="h-4 w-4" />
																<span className="truncate max-w-[200px]">{sol.address}</span>
															</div>
														)}
													</div>
												</div>

												<div className="flex flex-col sm:items-end gap-2">
													{effectiveStatus === "open" && !isPastDue && (
														<Button
															onClick={() => handleBidClick(sol.id, sol.title)}
														>
															Bid
														</Button>
													)}
													<Button variant="outline" size="sm" asChild>
														<Link
															to="/vendor/$buyerSlug/solicitations/$solicitationId"
															params={{ buyerSlug, solicitationId: sol.id }}
														>
															View Details
														</Link>
													</Button>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Existing Bid Confirmation Modal */}
			<Dialog
				open={!!existingBidModal?.open}
				onOpenChange={(open) => !open && setExistingBidModal(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>You Already Have a Bid</DialogTitle>
						<DialogDescription>
							You already have a bid on "{existingBidModal?.solicitationTitle}".
							{existingBidModal?.bidStatus === "draft"
								? " Your bid is still in draft mode and can be edited."
								: " Your bid has been submitted."}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="gap-2">
						<Button variant="outline" onClick={handleNewBid}>
							New Bid
						</Button>
						<Button onClick={handleExistingBidPrimary}>
							{existingBidModal?.bidStatus === "draft" ? "Edit Bid" : "View Bid"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</VendorLayout>
	);
}
